import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, ModalBody, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TranslateIcon } from 'assets/icon-translate.svg';
import { ReactComponent as ArrowExchangeIcon } from 'assets/icon-arrow-exchange.svg';

import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import AlertModal from 'shared/components/AlertModal';
import CustomSelect from 'shared/components/CustomSelect';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import MainSpinner from 'shared/components/MainSpinner';

import useApi from 'hooks/useApi';
import useLanguages from 'hooks/useLanguages';

import { showAlert, closeAlert } from 'redux/actions/alertActions';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, emptyString } from 'shared/utils';
import FieldType from 'shared/helpers/fieldType';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { colors } from 'shared/defaultTheme';

import { CustomModalWrapper } from './styles';

const TranslateFormModal = ({ onCloseModal, bootAsync }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cusNavId } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { customFields } = useSelector((state) => state.customField);
  const { customAreaApi } = useApi();
  const [showSuccessSave, setShowSuccessSave] = useState();
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [translatedCustomFields, setTranslatedCustomFields] = useState([]);
  const { languages } = useLanguages();

  const [{ loading: loadingPost }, postCustomFields] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleSuccessSave = () => setShowSuccessSave((currentShow) => !currentShow);

  useEffect(() => {
    setTranslatedCustomFields(customFields);
  }, [customFields]);

  const onChangeLanguage = (e) => {
    const { target } = e;
    const { value } = target;
    setSelectedLanguage(value);
  };

  const findName = (currentField, codeLang, dataDefault) => {
    const findI18n = currentField.i18List.find((i18object) => i18object.i18Code === codeLang);
    if (findI18n) {
      return findI18n.i18Name;
    }
    if (dataDefault && !findI18n) {
      return currentField.i18List[0].i18Name;
    }
    return '';
  };

  const onChangeValue = (event, field) => {
    const { target } = event;
    const { value } = target ?? { value: '' };
    let newFolder = {};
    if (value && value.length > 0) {
      const i18Item = field.i18List.find((i18object) => i18object.i18Code === selectedLanguage);
      if (i18Item) {
        newFolder = {
          ...field,
          i18List: [
            ...field.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      } else {
        newFolder = {
          ...field,
          i18List: [
            ...field.i18List,
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      }
    } else {
      newFolder = {
        ...field,
        i18List: [
          ...field.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
          {
            i18Code: selectedLanguage,
            i18name: '',
          },
        ],
      };
    }
    setTranslatedCustomFields(
      translatedCustomFields.map((currentField) => {
        if (currentField.id === newFolder.id) {
          return newFolder;
        }
        return currentField;
      })
    );
  };

  const onChangeValueMember = (event, field, member) => {
    const { target } = event;
    const { value } = target ?? { value: '' };
    let newMember = {};
    if (value && value.length > 0) {
      const i18Item = member.i18List.find((i18object) => i18object.i18Code === selectedLanguage);
      if (i18Item) {
        newMember = {
          ...member,
          i18List: [
            ...member.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      } else {
        newMember = {
          ...member,
          i18List: [
            ...member.i18List,
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      }
    } else {
      newMember = {
        ...member,
        i18List: [
          ...member.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
          {
            i18Code: selectedLanguage,
            i18name: '',
          },
        ],
      };
    }
    setTranslatedCustomFields(
      translatedCustomFields.map((currentField) => {
        if (currentField.id === field.id) {
          const newMembers = currentField.members.map((currentMember) => {
            if (currentMember.id === newMember.id) {
              return newMember;
            }
            return currentMember;
          });
          return {
            ...currentField,
            members: newMembers,
          };
        }
        return currentField;
      })
    );
  };

  const translate = async () => {
    try {
      const {
        data: { errors },
      } = await postCustomFields({
        url: `${window.API_V1}/cus/fields/${cusNavId}/modify`,
        data: translatedCustomFields,
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
      onToggleSuccessSave();
      bootAsync();
    } catch (ex) {
      console.error('Error on translate custom fields: ', ex);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
    }
  };

  const onOkSuccessSave = () => {
    onToggleSuccessSave();
    dispatch(closeAlert());
    onCloseModal();
  };

  return (
    <CustomModalWrapper className="translate-modal" isOpen toggle={onCloseModal} size="lg">
      <ModalHeader
        className="modal-header"
        backgroundColor={colors.transparent}
        onClose={onCloseModal}
      />
      <ModalBody>
        <Row className="row-title remove-margin-horizontal mb-2">
          <Col className="d-flex flex-row align-items-center" sm={12}>
            <TranslateIcon />
            <h3 className="text-uppercase">{t('custom_field_translate_title')}</h3>
          </Col>
        </Row>
        <Row className="remove-margin-horizontal mb-4">
          <Col className="d-flex flex-row justify-content-end remove-padding-horizontal" sm={12}>
            <CustomSelect
              defaultValue={
                emptyString(selectedLanguage) ? selectedDefaultLanguage : selectedLanguage
              }
              options={languages}
              onChange={onChangeLanguage}
            />
          </Col>
        </Row>
        <Row className="remove-margin-horizontal mb-4">
          <Col className="pl-0" sm={6}>
            <div className="column-name-wrapper text-uppercase">
              {t('custom_field_translate_column_field')}
            </div>
          </Col>
          <Col className="pr-0" sm={6}>
            <div className="column-name-wrapper text-uppercase">
              {t('custom_field_translate_column_translation')}
            </div>
          </Col>
        </Row>
        {translatedCustomFields.length > 0 && (
          <form className="form" noValidate>
            {translatedCustomFields
              .filter((customField) => !customField.parentId)
              .map((customField) => (
                <React.Fragment key={customField.id}>
                  <div className="card-fields mb-3">
                    <Row className="remove-margin-horizontal mb-1">
                      <Col className="remove-padding-horizontal" sm={12}>
                        <span>{t('custom_field_translate_title_field_group')}</span>
                      </Col>
                    </Row>
                    <Row className="remove-margin-horizontal">
                      <Col className="pl-0" sm={6}>
                        <div className="form__form-group">
                          <TextField value={findName(customField, selectedDefaultLanguage, true)} />
                        </div>
                      </Col>
                      <Col className="d-flex flex-row remove-padding-horizontal" sm={6}>
                        <ArrowExchangeIcon
                          className="translate-icon mr-3 mt-2"
                          fill={colors.suvaGrey}
                        />
                        <div className="form__form-group">
                          <TextField
                            value={findName(
                              customField,
                              selectedLanguage || selectedDefaultLanguage,
                              false
                            )}
                            onChange={(event) => {
                              if (
                                !emptyString(selectedLanguage) &&
                                selectedLanguage !== selectedDefaultLanguage
                              ) {
                                onChangeValue(event, customField);
                              }
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                    {(customField.type === FieldType.LIST ||
                      customField.type === FieldType.MULTI_LIST) && (
                      <div>
                        <Row className="remove-margin-horizontal mb-1">
                          <Col className="remove-padding-horizontal" sm={12}>
                            <span>{t('general_option_list')}</span>
                          </Col>
                        </Row>
                        {customField?.members.map((member) => (
                          <React.Fragment key={member.id}>
                            <Row className="remove-margin-horizontal">
                              <Col className="pl-0" sm={6}>
                                <div className="form__form-group">
                                  <TextField
                                    value={findName(member, selectedDefaultLanguage, true)}
                                  />
                                </div>
                              </Col>
                              <Col className="d-flex flex-row remove-padding-horizontal" sm={6}>
                                <ArrowExchangeIcon
                                  className="translate-icon mr-3 mt-2"
                                  fill={colors.suvaGrey}
                                />
                                <div className="form__form-group">
                                  <TextField
                                    value={findName(
                                      member,
                                      selectedLanguage || selectedDefaultLanguage,
                                      false
                                    )}
                                    onChange={(event) => {
                                      if (
                                        !emptyString(selectedLanguage) &&
                                        selectedLanguage !== selectedDefaultLanguage
                                      ) {
                                        onChangeValueMember(event, customField, member);
                                      }
                                    }}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {customField.type === FieldType.GROUP &&
                      translatedCustomFields
                        .filter((customFieldChild) => customFieldChild.parentId === customField.id)
                        .map((customFieldChild) => (
                          <React.Fragment key={customFieldChild.id}>
                            <Row className="remove-margin-horizontal mb-1">
                              <Col className="remove-padding-horizontal" sm={12}>
                                <span>{t('custom_field_translate_title_field')}</span>
                              </Col>
                            </Row>
                            <Row className="remove-margin-horizontal">
                              <Col className="pl-0" sm={6}>
                                <div className="form__form-group">
                                  <TextField
                                    value={findName(
                                      customFieldChild,
                                      selectedDefaultLanguage,
                                      true
                                    )}
                                  />
                                </div>
                              </Col>
                              <Col className="d-flex flex-row remove-padding-horizontal" sm={6}>
                                <ArrowExchangeIcon
                                  className="translate-icon mr-3 mt-2"
                                  fill={colors.suvaGrey}
                                />
                                <div className="form__form-group">
                                  <TextField
                                    value={findName(
                                      customFieldChild,
                                      selectedLanguage || selectedDefaultLanguage,
                                      false
                                    )}
                                    onChange={(event) => {
                                      if (
                                        !emptyString(selectedLanguage) &&
                                        selectedLanguage !== selectedDefaultLanguage
                                      ) {
                                        onChangeValue(event, customFieldChild);
                                      }
                                    }}
                                  />
                                </div>
                              </Col>
                            </Row>
                            {(customFieldChild.type === FieldType.LIST ||
                              customFieldChild.type === FieldType.MULTI_LIST) && (
                              <div>
                                <Row className="remove-margin-horizontal mb-1">
                                  <Col className="remove-padding-horizontal" sm={12}>
                                    <span>{t('general_option_list')}</span>
                                  </Col>
                                </Row>
                                {customFieldChild?.members.map((member) => (
                                  <React.Fragment key={member.id}>
                                    <Row className="remove-margin-horizontal">
                                      <Col className="pl-0" sm={6}>
                                        <div className="form__form-group">
                                          <TextField
                                            value={findName(member, selectedDefaultLanguage, true)}
                                          />
                                        </div>
                                      </Col>
                                      <Col
                                        className="d-flex flex-row remove-padding-horizontal"
                                        sm={6}
                                      >
                                        <ArrowExchangeIcon
                                          className="translate-icon mr-3 mt-2"
                                          fill={colors.suvaGrey}
                                        />
                                        <div className="form__form-group">
                                          <TextField
                                            value={findName(
                                              member,
                                              selectedLanguage || selectedDefaultLanguage,
                                              false
                                            )}
                                            onChange={(event) => {
                                              if (
                                                !emptyString(selectedLanguage) &&
                                                selectedLanguage !== selectedDefaultLanguage
                                              ) {
                                                onChangeValueMember(
                                                  event,
                                                  customFieldChild,
                                                  member
                                                );
                                              }
                                            }}
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                  </div>
                </React.Fragment>
              ))}
            <Row className="w-100 remove-margin-horizontal">
              <Col
                className="d-flex flex-row justify-content-end remove-padding-horizontal"
                sm={12}
              >
                <Button
                  className="text-uppercase mr-4"
                  type="button"
                  color="secondary"
                  label={t('general_button_cancel')}
                  onClick={onCloseModal}
                />
                <Button
                  className="text-uppercase"
                  type="button"
                  color="primary"
                  label={t('general_button_save')}
                  onClick={translate}
                  disabled={
                    emptyString(selectedLanguage) || selectedLanguage === selectedDefaultLanguage
                  }
                />
              </Col>
            </Row>
          </form>
        )}
        {loadingPost && <MainSpinner />}
      </ModalBody>
      <AlertModal
        type="success"
        isOpen={showSuccessSave}
        toggle={onOkSuccessSave}
        message={t('general_success_message')}
        okText={t('general_button_ok')}
        onOk={onOkSuccessSave}
      />
    </CustomModalWrapper>
  );
};

TranslateFormModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  bootAsync: PropTypes.func.isRequired,
};

export default TranslateFormModal;
